import { SettingsDetails } from '@pershing/pershing-core';
import * as moment from 'moment-timezone';

/**
 * PershingPreferenceUtil
 * Class having static methods to work with preference settings
 */
export class PershingPreferenceUtil {
    /**
     * Utility method to parse the settings Detail response
     *          for the input functionName and functionType
     * and return the GridSettingsForCurrentView
     *          settingsItem - grid column view settings for the input key
     *                  if key is not provided, 'default' string will be used as the key
     * @param settingsDetails
     * @param preferenceKey
     * @param functionName
     * @param functionType
     */
    static readonly US_TIMEZONE = 'America/New_York';
    static readonly SAVE_DATETIME_FORMAT = 'DD-MMM-YYYY hh:mm:ss a'; // moment js time format

    static getGridViewSettings(settingsDetails: SettingsDetails, functionName: string, functionType?: string, preferenceKey?: string): any {
        let gridSettingsForCurrentView: any = null;
        const GRID_SETTINGS_VIEW_KEY = 'grid-view-settings';
        const DEFAULT_KEY = 'default';
        /* currently functionType is defaulted to 'ApplicationSettings'
         *      as this is the default type supported by DB
        */
        if (functionName && functionName.trim() !== '') { // check input functionName as this is mandatory for differentiating each feature settings
            if (settingsDetails && settingsDetails.isAvailable) {
                if (settingsDetails.functionName && settingsDetails.functionName === functionName) {
                    if (settingsDetails.settings) {
                        if (settingsDetails.settings[GRID_SETTINGS_VIEW_KEY]) {
                            if (preferenceKey && preferenceKey.trim() !== '') {
                                gridSettingsForCurrentView = settingsDetails.settings[GRID_SETTINGS_VIEW_KEY][preferenceKey];
                            } else {
                                gridSettingsForCurrentView = settingsDetails.settings[GRID_SETTINGS_VIEW_KEY][DEFAULT_KEY];
                            }
                        }
                    }
                }
            }
        }
        return gridSettingsForCurrentView;
    }

    /**
     * Utility method to parse the settings Detail response
     *          for the input functionName and functionType
     * and return the SettingsDetails
     *          SettingsDetails - whole settings Details with prefilled settings inputs
     *
     * @param settingsDetails
     * @param functionName
     * @param functionType
     */
    static getPreferenceSettings(settingsDetails: SettingsDetails, functionName: string, functionType?: string) {
        let preferenceSettings: SettingsDetails = null;
        /* currently functionType is defaulted to 'ApplicationSettings'
         *      as this is the default type supported by DB
        */
        if (functionName && functionName.trim() !== '') { // check input functionName as this is mandatory for differentiating each feature settings
            if (settingsDetails && settingsDetails.isAvailable) {
                if (settingsDetails.functionName && settingsDetails.functionName === functionName) {
                    preferenceSettings = settingsDetails;
                }
            }

            if (!preferenceSettings) {
                // default settings to pass to pershing grid controller - settings will be used to "firstime -> save" the information for the component.
                preferenceSettings = <SettingsDetails>({'settings' : null, 'functionName' : functionName,
                            'functionType' : functionType != null ? functionType  : 'ApplicationSettings',
                            'dataSize': null, 'isAvailable': false,
                            'taskName': null, 'uniqueId': null, 'updatedTimeStamp': null });
            }
        }
        return preferenceSettings;
    }

    /**
     * Method to apply preferences to the default grid columns definition
     * @param columnDefinition
     * @param columnPreferences
     * @memberof PershingGridUtil
     */
    static applyColumnPreferences(columnDef: any, columnPreferences: any) {
        let columnDefinition = [];
        Object.assign(columnDefinition, columnDef);
        if ((columnDefinition && columnDefinition.length > 0) && columnPreferences) {
            try {
                // get column unique ids from columnPreferences
                let orderedPrefColIds = columnPreferences.map ( (preference) => {
                    return preference.colId;
                });

                // filter the default columns from feature with preferences
                // then apply visibility
                /*
                 if default column list contains columns other than the saved list and it is not
                 maintained by column component (hideInColumns - true), they will be still available in the column list in the same
                 position as in default list. If in case saved columns count is less than index of such columns,
                 those columns will be available in last.
                 If the new column is maintained by column component it will be hidden by default
                */
                let filteredColSet = columnDefinition.filter((col, index) => {
                    let colUniqueId  = col.colId ? col.colId : col.field;
                    let colMatcherResult = false;
                    colMatcherResult = columnPreferences.some((preference) => {
                        if (colUniqueId === preference.colId) {
                            // apply preferences width to the column after matching the colId
                            if (preference.width && preference.width != null) {
                                col.width = preference.width;
                            }
                            // apply preferences visibility to the column
                            col.hide = !preference.visibility;
                            return true;
                        } else {
                            return false;
                        }
                    });
                    /*
                        Insert the colid of newly added column in orderedPrefColIds in the same position as in default config.
                        If orderedPrefColIds count is less than index of new column add the new coldId as last in the array.
                    */
                    if (!colMatcherResult) {
                        // By default show the new columns if it is not displayed in column component else hide it
                        if (!col.hideInColumns && col.hideInColumns !== true) {
                            col.hide = true;
                        }
                        // New columns should be added in same postion in as in column definition
                        if (orderedPrefColIds && (orderedPrefColIds.length - 1) >= index) {
                            orderedPrefColIds.splice(index, 0, colUniqueId);
                        } else {
                            orderedPrefColIds.splice(orderedPrefColIds.length, 0, colUniqueId);
                        }
                        colMatcherResult = !colMatcherResult;
                    }
                    return colMatcherResult;
                });
                // sort the columns in the order of prefereces
                filteredColSet.sort( (col1, col2) => {
                    let col1UniqueId = col1.colId ? col1.colId : col1.field;
                    let col2UniqueId = col2.colId ? col2.colId : col2.field;
                    return orderedPrefColIds.indexOf(col1UniqueId) - orderedPrefColIds.indexOf(col2UniqueId);
                });
                return filteredColSet;
            } catch (e) {
                // console.log('Error in applying preferences to grid column settings : ', e); // replace with logger
                return columnDefinition;
            }
        } else {
            // console.log('Either Grid settings or preferences un-available'); // replace with logger
            return columnDefinition;
        }
    }

    /**
     * method to prepare columnState from the current grid state
     *
     * @param columnState
     * @returns
     * @memberof PershingPreferenceUtil
     */
    static prepareColumnStateToSave(columnState, widthPreferences?: any) {
        /* Final colState will be in the format of
            Array of ColumnState objects
                    column state will be object - currently having colId (columnUniqueId) and
                    visibility (show/hide status)
                    width (changed/resized column width)
                    can be extended in future to store other column states
        */
        let columnStateToSave = columnState.map(column => {
            let widthOfColumn = widthPreferences ? (widthPreferences[column.colId] ? widthPreferences[column.colId] : null) : null;
            let colState = {'colId': column.colId, 'visibility': !column.hide, 'width': widthOfColumn};
            return colState;
         });
        return columnStateToSave;
    }

    /**
     * method to prepare gridColumnSettings before save
     *
     * @param settingsDetails
     * @param preferenceViewKey
     * @param columnState
     * @returns
     * @memberof PershingPreferenceUtil
     */
    static packGridSettings(settingsDetails: SettingsDetails, preferenceViewKey: string, columnState: any): any {
        const GRID_SETTINGS_VIEW_KEY = 'grid-view-settings';
        const DEFAULT_KEY = 'default';
        let prefSettings = settingsDetails.settings;
        /*  Below will be the grid settings format
         *    {
         *		'grid-view-settings' :
         *		  {
         *           'default' : [{colId:'col1', visibility : false, width : 250}, {colId:'col2', visibility : false}]
         *           'account' : [{colId:'col1', visibility : false, width : 250}]
         *			 'account-transaction-view' : [{colId:'col1', visibility : false, width : 250}, {colId:'col2', visibility : false}, ..],
         *			  ....
         *         }
         *     }
        */
        if (!prefSettings || prefSettings == null) { // if no initial settings available - first time save
            prefSettings = <any> ({});
            prefSettings[GRID_SETTINGS_VIEW_KEY] = <any>({});
        } else {
            if (! prefSettings[GRID_SETTINGS_VIEW_KEY]) {
                prefSettings[GRID_SETTINGS_VIEW_KEY] = <any>({});
            }
        }
        let prefKey: string = (preferenceViewKey && preferenceViewKey.trim() !== '') ? preferenceViewKey : DEFAULT_KEY;
        prefSettings[GRID_SETTINGS_VIEW_KEY][prefKey] = columnState;
        return prefSettings;
    }

    /**
     * method to get current datetime stamp
     *        uses moment js to generate and format the current datetime
     *        timezone defaulted to NEW_YORK time zone
     *        output date format (moment js time format) - DD-MMM-YYYY hh:mm:ss a
     *        return datetime string in uppercase
     *              sample output : 08-APR-2019 04:54:08 AM
     *
     * currently DB settings service needs timestamp input in the above format
     */
    static getCurrentTimestamp() {
        let currentDate: Date = new Date();
        let currentDateTimeString = moment.tz(currentDate, PershingPreferenceUtil.US_TIMEZONE).format(PershingPreferenceUtil.SAVE_DATETIME_FORMAT);
        return currentDateTimeString.toUpperCase();
    }
}
